import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query/keys.js';
import { fetchJson } from '../lib/query/fetchJson.js';
import { apiUrl } from '../utils/api.js';
import type {
  BillingSubscriptionSnapshot,
  BillingVerificationSnapshot,
} from '../components/Settings/billing-ui.js';
import { hasActiveSubscriptionStatus } from '../components/Settings/billing-ui.js';

export type BillingSnapshotResponse = {
  subscription: BillingSubscriptionSnapshot;
  customer?: {
    providerCustomerId: string;
    provider: string;
  } | null;
  verification?: BillingVerificationSnapshot;
};

const BILLING_SNAPSHOT_STALE_MS = 5 * 60_000;
const BILLING_VERIFICATION_POLL_MS = 15_000;

interface UseBillingSnapshotOptions {
  userId: string | null;
  isBypassSession?: boolean;
  refreshOnReturn?: boolean;
  enabled?: boolean;
  refetchWhenVerificationPending?: boolean;
}

export function getCheckoutReturnToken(search: string | null | undefined): string | null {
  const params = new URLSearchParams(search ?? '');
  const token = params.get('customer_session_token');
  if (token && token.length > 0) {
    return token;
  }

  const billingReturn = params.get('billing_return');
  if (billingReturn === '1' || billingReturn === 'true') {
    return 'billing_return';
  }

  const authReturn = params.get('auth_return');
  return authReturn === '1' || authReturn === 'true' ? 'auth_return' : null;
}

export function buildBillingSnapshotPath(refreshOnReturn: boolean): string {
  return refreshOnReturn ? '/billing/subscription?refresh=1' : '/billing/subscription';
}

export function shouldPollBillingVerification(
  data: BillingSnapshotResponse | null | undefined,
): boolean {
  if (!data || data.verification?.state !== 'pending') {
    return false;
  }

  return !hasActiveSubscriptionStatus(data.subscription?.status ?? null);
}

function clearCheckoutReturnTokenFromUrl() {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  const hasCustomerSessionToken = url.searchParams.has('customer_session_token');
  const hasBillingReturnMarker = url.searchParams.has('billing_return');
  const hasAuthReturnMarker = url.searchParams.has('auth_return');

  if (!hasCustomerSessionToken && !hasBillingReturnMarker && !hasAuthReturnMarker) {
    return;
  }

  url.searchParams.delete('customer_session_token');
  url.searchParams.delete('billing_return');
  url.searchParams.delete('auth_return');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

export function useBillingSnapshot({
  userId,
  isBypassSession = false,
  refreshOnReturn = false,
  enabled = true,
  refetchWhenVerificationPending = true,
}: UseBillingSnapshotOptions) {
  useEffect(() => {
    if (!refreshOnReturn) {
      return;
    }

    clearCheckoutReturnTokenFromUrl();
  }, [refreshOnReturn]);

  return useQuery({
    queryKey: userId ? queryKeys.billingSnapshot(userId) : (['billing', 'subscription', 'anonymous'] as const),
    queryFn: async () =>
      fetchJson<BillingSnapshotResponse>(
        apiUrl(buildBillingSnapshotPath(refreshOnReturn)),
        { credentials: 'include' },
        'Failed to load billing state',
      ),
    enabled: enabled && Boolean(userId) && !isBypassSession,
    staleTime: BILLING_SNAPSHOT_STALE_MS,
    refetchOnMount: refreshOnReturn ? 'always' : false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      if (!refetchWhenVerificationPending) {
        return false;
      }

      return shouldPollBillingVerification(query.state.data as BillingSnapshotResponse | undefined)
        ? BILLING_VERIFICATION_POLL_MS
        : false;
    },
  });
}
